import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getTimezoneFromCoordinates } from '@delmonicos/shared-sdk/libs/core/src/helpers/get-timezone-from-coordinates';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getCreateLocationDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/create-location.form-default-values';
import { CreateLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/create-location.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { LocationCreateFormController } from '@app/feature/location/location-create/location-create-form/location-create-form.controller';
import { CreateLocationContextProvider } from '@app/feature/location/location.context';
import { useCpmsBoCreateLocationMutation } from '@service/cpms-bo-location.service';

interface LocationCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const LocationCreateController = ({
  onCancel,
  onSuccess,
}: LocationCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createLocationMutation] = useCpmsBoCreateLocationMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateLocationModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.location-create.error'));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<CreateLocationModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    // Recomputed here rather than trusted from the debounced form field —
    // the field's derivation can still be in flight when the user submits
    // right after typing coordinates, so this guarantees the submitted
    // timeZone always matches the submitted coordinates.
    const createLocation = removeEmptyFields({
      ...fieldValues,
      timeZone: getTimezoneFromCoordinates(
        fieldValues.coordinates?.latitude,
        fieldValues.coordinates?.longitude,
      ),
    });

    createLocationMutation(createLocation)
      .unwrap()
      .then(() => {
        toast.success(t('$.location-create.success'));
        onSuccess();
      })
      .catch((error) => {
        Logger.error(error);
        toast.error(
          t('$.location-create.error', { error: t('global.unknown') }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER //////////////////////////////////////////////////////////////////

  const data = getCreateLocationDefaultValues();

  return (
    <CreateLocationContextProvider data={data}>
      <LocationCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateLocationContextProvider>
  );
};
